import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useSubscription } from '../hooks/useSubscription';
import { VenueAdSource, VenueAdsConfig } from '../types';

const defaultConfig: VenueAdsConfig = {
  enabled: false,
  placement: 'full-screen',
  defaultDurationSeconds: 25,
  minIntervalMinutes: 5,
  triggers: {
    whenNoQueue: true,
    afterSongsCount: 0,
    duringSplash: true,
  },
  sources: [],
};

interface VenueAdsManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

const VenueAdsManager: React.FC<VenueAdsManagerProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const subscription = useSubscription();
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [config, setConfig] = useState<VenueAdsConfig>(defaultConfig);
  const isPremium = subscription.isActive && subscription.tier !== 'free';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!user) { setLoading(false); return; }
      try {
        setLoading(true);
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (snap.exists()) {
          const d = snap.data() as any;
          const c = (d.venueAdsConfig || {}) as Partial<VenueAdsConfig>;
          const merged: VenueAdsConfig = {
            ...defaultConfig,
            ...c,
            triggers: { ...defaultConfig.triggers, ...(c.triggers || {}) },
            sources: Array.isArray(c.sources) ? (c.sources as any[]).map((s, idx) => ({
              id: s.id || String(idx + 1),
              type: (s.type === 'video' ? 'video' : 'image'),
              url: s.url || '',
              enabled: s.enabled !== false,
              label: s.label || '',
              durationSeconds: s.durationSeconds,
            })) : [],
          };
          if (!cancelled) setConfig(merged);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [user?.uid]);

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), { venueAdsConfig: config });
      alert('Venue ads settings saved.');
      onClose();
    } catch (e) {
      console.error('Failed to save venue ads config', e);
      alert('Failed to save venue ads settings.');
    } finally {
      setSaving(false);
    }
  };

  const addSource = () => {
    const next: VenueAdSource = {
      id: crypto.randomUUID(),
      type: 'image',
      url: '',
      enabled: true,
      label: '',
    };
    setConfig((prev) => ({ ...prev, sources: [...prev.sources, next] }));
  };

  const updateSource = (id: string, patch: Partial<VenueAdSource>) => {
    setConfig((prev) => ({
      ...prev,
      sources: prev.sources.map((s) => (s.id === id ? { ...s, ...patch } : s)),
    }));
  };

  const removeSource = (id: string) => {
    setConfig((prev) => ({ ...prev, sources: prev.sources.filter((s) => s.id !== id) }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto text-white">
        <div className="flex justify-between items-center p-6 border-b border-gray-700">
          <h2 className="text-3xl font-bold">📺 Venue Ads</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-3xl font-bold">×</button>
        </div>

        {!isPremium && (
          <div className="m-6 p-4 rounded-lg bg-gray-700 border border-gray-600 text-gray-200">
            Venue ads are available for premium accounts. Upgrade to configure and show your own ads between songs.
          </div>
        )}

        <div className="p-6 space-y-6 opacity-100">
          <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
            <div>
              <h3 className="text-lg font-semibold">Enable Venue Ads</h3>
              <p className="text-gray-300 text-sm">When enabled, your configured ads can play during song breaks.</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" disabled={!isPremium}
                checked={!!config.enabled}
                onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-700 rounded-lg p-4 space-y-3">
              <h3 className="text-lg font-semibold">Behavior</h3>
              <label className="flex items-center justify-between text-sm">
                <span>Placement</span>
                <select disabled={!isPremium}
                  value={config.placement}
                  onChange={(e) => setConfig({ ...config, placement: e.target.value as any })}
                  className="p-2 bg-gray-800 rounded border border-gray-600"
                >
                  <option value="full-screen">Full Screen</option>
                  <option value="lower-third">Lower Third</option>
                </select>
              </label>
              <label className="flex items-center justify-between text-sm">
                <span>Default Duration (s)</span>
                <input disabled={!isPremium}
                  type="number" min={5} max={120}
                  value={config.defaultDurationSeconds}
                  onChange={(e) => setConfig({ ...config, defaultDurationSeconds: Math.max(5, Number(e.target.value || 0)) })}
                  className="w-24 p-2 bg-gray-800 rounded border border-gray-600 text-right"
                />
              </label>
              <label className="flex items-center justify-between text-sm">
                <span>Min Interval (min)</span>
                <input disabled={!isPremium}
                  type="number" min={0} max={120}
                  value={config.minIntervalMinutes}
                  onChange={(e) => setConfig({ ...config, minIntervalMinutes: Math.max(0, Number(e.target.value || 0)) })}
                  className="w-24 p-2 bg-gray-800 rounded border border-gray-600 text-right"
                />
              </label>
            </div>
            <div className="bg-gray-700 rounded-lg p-4 space-y-3">
              <h3 className="text-lg font-semibold">Auto Triggers</h3>
              <label className="flex items-center justify-between text-sm">
                <span>When no songs in queue</span>
                <input disabled={!isPremium}
                  type="checkbox"
                  checked={config.triggers.whenNoQueue}
                  onChange={(e) => setConfig({ ...config, triggers: { ...config.triggers, whenNoQueue: e.target.checked } })}
                />
              </label>
              <label className="flex items-center justify-between text-sm gap-2">
                <span>After X songs</span>
                <input disabled={!isPremium}
                  type="number" min={0} max={20}
                  value={config.triggers.afterSongsCount}
                  onChange={(e) => setConfig({ ...config, triggers: { ...config.triggers, afterSongsCount: Math.max(0, Number(e.target.value || 0)) } })}
                  className="w-24 p-2 bg-gray-800 rounded border border-gray-600 text-right"
                />
              </label>
              <label className="flex items-center justify-between text-sm">
                <span>During splash only</span>
                <input disabled={!isPremium}
                  type="checkbox"
                  checked={config.triggers.duringSplash}
                  onChange={(e) => setConfig({ ...config, triggers: { ...config.triggers, duringSplash: e.target.checked } })}
                />
              </label>
            </div>
          </div>

          <div className="bg-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Sources</h3>
              <button onClick={addSource} disabled={!isPremium} className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 px-3 py-2 rounded">
                ➕ Add Source
              </button>
            </div>
            {config.sources.length === 0 ? (
              <div className="mt-4 p-4 bg-black/30 border border-dashed border-gray-600 rounded text-sm text-gray-300">
                No sources added yet. Add image or video URLs to show during breaks.
              </div>
            ) : (
              <div className="mt-3 space-y-3">
                {config.sources.map((s) => (
                  <div key={s.id} className="p-3 rounded bg-gray-800 border border-gray-700 flex flex-col gap-2">
                    <div className="flex items-center gap-2 justify-between">
                      <div className="flex items-center gap-2">
                        <input type="checkbox" disabled={!isPremium} checked={s.enabled} onChange={(e) => updateSource(s.id, { enabled: e.target.checked })} />
                        <select disabled={!isPremium} value={s.type} onChange={(e) => updateSource(s.id, { type: e.target.value as any })} className="p-1 bg-gray-900 rounded border border-gray-700">
                          <option value="image">Image</option>
                          <option value="video">Video</option>
                        </select>
                      </div>
                      <button onClick={() => removeSource(s.id)} disabled={!isPremium} className="text-sm px-2 py-1 rounded bg-red-700 hover:bg-red-800 disabled:bg-gray-700">Remove</button>
                    </div>
                    <input disabled={!isPremium}
                      type="url" value={s.url}
                      onChange={(e) => updateSource(s.id, { url: e.target.value })}
                      placeholder={s.type === 'video' ? 'https://example.com/video.mp4' : 'https://example.com/image.jpg'}
                      className="w-full p-2 bg-gray-900 rounded border border-gray-700 text-sm"
                    />
                    <div className="flex items-center gap-3">
                      <input disabled={!isPremium}
                        type="text" value={s.label || ''} onChange={(e) => updateSource(s.id, { label: e.target.value })}
                        placeholder="Label (optional)" className="flex-1 p-2 bg-gray-900 rounded border border-gray-700 text-sm" />
                      <label className="text-sm flex items-center gap-2">
                        Per-item Duration (s)
                        <input disabled={!isPremium}
                          type="number" min={0} max={120} value={s.durationSeconds || 0}
                          onChange={(e) => updateSource(s.id, { durationSeconds: Math.max(0, Number(e.target.value || 0)) })}
                          className="w-20 p-2 bg-gray-900 rounded border border-gray-700 text-right" />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-between p-6 border-t border-gray-700">
          <button onClick={onClose} className="bg-gray-600 hover:bg-gray-700 px-6 py-3 rounded-lg font-semibold transition-colors">Close</button>
          <button onClick={handleSave} disabled={saving || !isPremium} className="bg-qr-primary hover:bg-qr-secondary disabled:bg-gray-600 px-6 py-3 rounded-lg font-semibold transition-colors">{saving ? 'Saving…' : 'Save Venue Ads'}</button>
        </div>
      </div>
    </div>
  );
};

export default VenueAdsManager;
